import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "../../../component/element/table";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  delete_lawyer,
  get_lawyers,
  toggle_lawyer_status,
} from "../../../services/apis/lawyers";
import { ILawyer } from "../../../store/types/lawyers";
import ItemAction from "../../../component/ui/action/ItemAction";
import ProfessionalViewModal from "../show/ProfessionalViewModal";
import LawyerFormModal from "./LawyerFormModal";

interface ILawyersTable {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  createOpen: boolean;
  onCreateClose: () => void;
}

const LawyersTable = ({
  page,
  setPage,
  createOpen,
  onCreateClose,
}: ILawyersTable) => {
  const dispatch = useAppDispatch();
  const [viewingLawyerId, setViewingLawyerId] = React.useState<number | null>(
    null,
  );
  const [editingLawyer, setEditingLawyer] = React.useState<ILawyer | null>(
    null,
  );
  const { lawyers, total, limit, loading, loadingLawyerId } = useAppSelector(
    ({ lawyers }) => lawyers,
  );
  const lastPage = Math.max(1, Math.ceil(total / limit));

  React.useEffect(() => {
    if (page > lastPage) {
      setPage(lastPage);
      return;
    }
    get_lawyers(dispatch, (page - 1) * limit, limit);
  }, [dispatch, lastPage, limit, page, setPage]);

  const columns: ColumnDef<ILawyer>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => {
        const name = (getValue() as string) || "—";
        return <span className="font-medium text-gray-900">{name}</span>;
      },
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone_number",
      header: "Phone Number",
      cell: ({ getValue }) => (getValue() as string) || "—",
    },
    {
      accessorKey: "profile_status",
      header: "Profile Status",
      cell: ({ getValue }) => {
        const status = (getValue() as string) || "—";
        const isApproved = status.toLowerCase() === "approved";
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
              isApproved
                ? "bg-primary-100/20 text-primary-700"
                : "bg-amber-100/20 text-amber-700"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Account",
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const { id, is_active: isActive } = row.original;
        const isUpdating = loadingLawyerId === id;
        return (
          <ItemAction
            show_view
            show_edit
            show_delete
            show_toggle
            toggle_checked={isActive}
            toggle_loading={isUpdating}
            delete_loading={isUpdating}
            onView={() => setViewingLawyerId(id)}
            onEdit={() => setEditingLawyer(row.original)}
            onDelete={() => {
              if (
                window.confirm("Are you sure you want to delete this lawyer?")
              ) {
                void delete_lawyer(dispatch, id);
              }
            }}
            onToggle={(checked) => toggle_lawyer_status(dispatch, id, checked)}
          />
        );
      },
    },
  ];

  return (
    <>
      {loading && (
        <p className="mb-2 text-sm text-gray-500" role="status">
          Loading lawyers...
        </p>
      )}
      <DataTable
        columns={columns}
        data={lawyers}
        current_page={page}
        last_page={lastPage}
        onNext={() => page < lastPage && setPage(page + 1)}
        onPrev={() => page > 1 && setPage(page - 1)}
      />
      <ProfessionalViewModal
        open={viewingLawyerId !== null}
        type="lawyer"
        id={viewingLawyerId}
        onClose={() => setViewingLawyerId(null)}
      />
      <LawyerFormModal
        open={createOpen || editingLawyer !== null}
        lawyer={editingLawyer}
        onClose={() => {
          setEditingLawyer(null);
          onCreateClose();
        }}
      />
    </>
  );
};

export default LawyersTable;
